/**
 * Restores scroll position and infinite-scroll item count when returning
 * to a collection page via browser back navigation.
 *
 * Without this, infinite-scrolled items are lost (they aren't in the URL)
 * and the page reloads from page 1, dropping the user at the top.
 */
(function () {
  const STORAGE_KEY = 'collectionScrollRestore';
  const EXPIRY_MS = 30 * 60 * 1000;
  const MAX_FETCH_REPLAYS = 30;
  const HEIGHT_WAIT_MS = 4000;
  const IMAGE_WAIT_MS = 2000;
  const POLL_MS = 30;

  function currentKey() {
    return window.location.pathname + window.location.search;
  }

  function readStates() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  function writeStates(states) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(states));
    } catch {
      /* quota — ignore */
    }
  }

  function saveCurrentState() {
    if (!document.querySelector('infinite-scroll')) return;
    // Skip shallow scrolls — within one viewport, the natural "back to top"
    // landing is no worse than the restored position, so don't bother.
    if (window.scrollY < window.innerHeight * 1.5) return;
    const states = readStates();
    states[currentKey()] = {
      scrollY: window.scrollY,
      itemsLoaded: document.querySelectorAll('.js-pagination__item').length,
      timestamp: Date.now(),
    };
    writeStates(states);
  }

  function consumeState() {
    const states = readStates();
    const key = currentKey();
    const state = states[key];
    if (!state) return null;
    delete states[key];
    writeStates(states);
    if (Date.now() - state.timestamp > EXPIRY_MS) return null;
    return state;
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const BANNER_ID = 'collection-scroll-restore-banner';

  function showBanner() {
    if (document.getElementById(BANNER_ID)) return;
    const message =
      window.strings?.general?.pagination?.restoringPosition || 'Restoring your position…';
    const banner = document.createElement('div');
    banner.id = BANNER_ID;
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.innerHTML = `
      <span class="csr-spinner" aria-hidden="true"></span>
      <span class="csr-message"></span>
    `;
    banner.querySelector('.csr-message').textContent = message;

    const style = document.createElement('style');
    style.id = BANNER_ID + '-style';
    style.textContent = `
      #${BANNER_ID} {
        position: fixed;
        top: 1rem;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1000;
        display: inline-flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.6rem 1rem;
        background-color: rgb(var(--color-background, 255 255 255));
        color: rgb(var(--color-foreground, 0 0 0));
        border: 1px solid rgba(var(--color-foreground, 0 0 0), 0.12);
        border-radius: 999px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        font-size: 0.9rem;
        opacity: 0;
        animation: csr-fade-in 0.2s ease forwards;
      }
      #${BANNER_ID}.csr-fading-out {
        animation: csr-fade-out 0.2s ease forwards;
      }
      #${BANNER_ID} .csr-spinner {
        width: 1rem;
        height: 1rem;
        border: 2px solid rgba(var(--color-foreground, 0 0 0), 0.2);
        border-top-color: rgb(var(--color-foreground, 0 0 0));
        border-radius: 50%;
        animation: csr-spin 0.8s linear infinite;
      }
      @keyframes csr-spin { to { transform: rotate(360deg); } }
      @keyframes csr-fade-in { from { opacity: 0; transform: translate(-50%, -8px); } to { opacity: 1; transform: translate(-50%, 0); } }
      @keyframes csr-fade-out { to { opacity: 0; transform: translate(-50%, -8px); } }
    `;
    document.head.appendChild(style);
    document.body.appendChild(banner);
  }

  function hideBanner() {
    const banner = document.getElementById(BANNER_ID);
    if (!banner) return;
    banner.classList.add('csr-fading-out');
    setTimeout(() => {
      banner.remove();
      document.getElementById(BANNER_ID + '-style')?.remove();
    }, 220);
  }

  function getMainEl() {
    return document.querySelector('main');
  }

  // The lock class is applied pre-paint by the inline script in theme.liquid
  // (so <main> never paints visible). We just have to make sure it's set
  // when needed and remove it when restoration finishes.
  function lockUI() {
    document.documentElement.classList.add('csr-restoring');
  }

  function unlockUI() {
    const main = getMainEl();
    if (main) main.style.transition = 'opacity 0.2s ease';
    // Defer the class removal one frame so the transition is registered
    // before the opacity property change triggers it.
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('csr-restoring');
      if (main) {
        setTimeout(() => {
          main.style.transition = '';
        }, 250);
      }
    });
  }

  async function waitForInfiniteScroll(timeout = 2000) {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const el = document.querySelector('infinite-scroll');
      if (el && typeof el.fetchNextItems === 'function') return el;
      await sleep(POLL_MS);
    }
    return null;
  }

  async function loadEnoughItems(infiniteScroll, target) {
    let attempts = 0;
    while (
      document.querySelectorAll('.js-pagination__item').length < target &&
      attempts < MAX_FETCH_REPLAYS
    ) {
      // Wait for any in-flight fetch to finish before queueing the next one;
      // fetchNextItems returns undefined synchronously if fetchInProgress is true.
      while (infiniteScroll.fetchInProgress) await sleep(POLL_MS);
      const nextBtn = document.querySelector('.js-pagination__next-button[href]');
      if (!nextBtn) break;
      attempts += 1;
      await infiniteScroll.fetchNextItems();
    }
    while (infiniteScroll.fetchInProgress) await sleep(POLL_MS);
  }

  async function waitForVisibleImages(timeout) {
    const imgs = Array.from(document.querySelectorAll('.js-pagination__item img')).filter(
      (img) => !img.complete
    );
    if (imgs.length === 0) return;
    await Promise.race([
      Promise.all(
        imgs.map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
        )
      ),
      sleep(timeout),
    ]);
  }

  async function waitForDocumentHeight(minHeight, timeout) {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      if (document.documentElement.scrollHeight >= minHeight) return true;
      await new Promise((r) => requestAnimationFrame(r));
    }
    return false;
  }

  async function restoreState() {
    const state = consumeState();
    if (!state) return;

    const targetMinHeight = state.scrollY + window.innerHeight;
    const alreadyHave = document.querySelectorAll('.js-pagination__item').length;
    const needsFetching = alreadyHave < state.itemsLoaded;

    // bfcache fast path: items already there, just scroll once layout is ready.
    if (!needsFetching) {
      await waitForDocumentHeight(targetMinHeight, HEIGHT_WAIT_MS);
      window.scrollTo(0, state.scrollY);
      return;
    }

    showBanner();
    lockUI();

    try {
      const infiniteScroll = await waitForInfiniteScroll();
      if (infiniteScroll) {
        await loadEnoughItems(infiniteScroll, state.itemsLoaded);
        await waitForVisibleImages(IMAGE_WAIT_MS);
        await waitForDocumentHeight(targetMinHeight, HEIGHT_WAIT_MS);
      }
      // Scroll while grid is still hidden — programmatic scrollTo bypasses overflow:hidden.
      window.scrollTo(0, state.scrollY);
      // Allow one paint at the new scroll position before revealing the grid.
      await new Promise((r) => requestAnimationFrame(r));
    } finally {
      unlockUI();
      hideBanner();
    }
  }

  document.addEventListener(
    'click',
    (e) => {
      const link = e.target.closest('.product-card-wrapper a, .card-wrapper a');
      if (!link || !link.href) return;
      saveCurrentState();
    },
    true
  );

  if (document.readyState === 'complete') {
    restoreState();
  } else {
    window.addEventListener('load', restoreState, { once: true });
  }

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) restoreState();
  });
})();
